import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ReactImageGallery from "react-image-gallery";
import {
  faBed,
  faBath,
  faRulerCombined,
  faClock,
  faPhone,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import constants from "../constants/constants";
import placeholder from "../assets/images/house-placeholder.webp";
import SEO, { pageSEO } from "../components/seo.component";
import InquiryCard from "./inquiryCard.component";
import { useUserParams } from "../hooks/useUserParams";
// remove the below two lines from client websites. These are dummy data
import data from "../data.json";
import imgs from "./temp.json";

export default function Listing() {
  const { id } = useParams();
  const {
    getCurrentAgentName,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentHeadshot,
  } = useUserParams();

  const prop = data.value.find(prop => prop.ListingKey === id) //dummy listings
  const [property, setProperty] = useState(prop);

  // const [property, setProperty] = useState({});
  // const [images, setImages] = useState([]);

  //To create build with dummy listings
  const [images, setImages] = useState(property.Images
    .map(img => ({
      original:
        img,

      thumbnail:
        img
    }))
  );

  const [loading, setLoading] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [windowSize, setWindowSize] = useState(window.innerWidth);

  const onWindowResize = () => {
    setWindowSize(window.innerWidth);
  };
  useEffect(() => {
    window.addEventListener("resize", onWindowResize);
    return () => {
      window.removeEventListener("resize", onWindowResize);
    };
  }, []);

  async function fetchAPI(url) {
    const body = {
      url,
      needAuth: true,
      bearerToken: constants.TOKEN
    };

    const response =
      constants.ENVIRONMENT === "production"
        ?
        await fetch(
          `https://${constants.DOMAIN}/api/${url}`
        )
        :
        await fetch(
          `http://${constants.URL}/metaGet`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify(body)
          }
        );
    if (!response.ok)
      throw new Error("API Error");
    return response.json();
  }

  async function loadListing() {
    try {
      setLoading(true);

      const url = constants.ENVIRONMENT === "production" ?
        `odata/Property(Q)$filter=ListingKey(S)eq(S)'${id}'`
        :
        `https://query.ampre.ca/odata/Property?$filter=ListingKey eq '${id}'`;

      const propertyData =
        await fetchAPI(url);

      setProperty(
        propertyData.value?.[0] || {}
      );

      let mediaUrl;
      if (constants.ENVIRONMENT === "production") {
        mediaUrl =
          `odata/Media(Q)$filter=ResourceRecordKey(S)eq(S)'${id}'(S)and(S)(ImageSizeDescription(S)eq(S)'LargestNoWatermark'(S)or(S)ImageSizeDescription(S)eq(S)'Thumbnail')`;
      } else {
        mediaUrl =
          `https://query.ampre.ca/odata/Media?$filter=ResourceRecordKey eq '${id}' and (ImageSizeDescription eq 'LargestNoWatermark' or ImageSizeDescription eq 'Thumbnail')`;
      }

      const media =
        await fetchAPI(
          mediaUrl
        );

      console.log("media", media);

      const formatted =
        media.value?.reduce((acc, item) => {
          const existing =
            acc[item.MediaObjectID] || {};

          acc[item.MediaObjectID] = {
            ...existing,
            [item.ImageSizeDescription]: item
          };
          return acc;
        }, {});

      setImages(
        Object.values(formatted || {})
          .map(img => ({
            original:
              img?.LargestNoWatermark?.MediaURL || placeholder,

            thumbnail:
              img?.Thumbnail?.MediaURL || placeholder
          }))
      );
    }
    catch (err) {
      console.log(err);
    }
    finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadListing();
  }, []);

  const seoConfig =
    pageSEO.listing(property);

  return (
    <>
      <SEO {...seoConfig} />
      <section className="min-h-screen bg-summit-background pt-32 pb-20">
        <div className="max-w-[1400px] mx-auto px-6">

          <div className="flex flex-col lg:flex-row justify-between gap-8 mb-10">
            <div>
              <p className="uppercase tracking-[0.4em] text-sm text-summit-highlight">
                Property Details
              </p>
              <h1 className="mt-4 text-4xl md:text-5xl font-light text-summit-primary font-heading">
                {property?.StreetNumber} {property?.StreetName} {property?.StreetSuffix}
              </h1>
              <p className="mt-3 text-xl text-slate-500">
                {property?.City}, {property?.StateOrProvince} {property?.PostalCode}
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg px-8 py-6 h-fit">
              <p className="text-sm uppercase tracking-widest text-slate-400">
                Listing Price
              </p>
              <h2 className="mt-2 text-3xl font-semibold text-summit-primary">
                ${property?.ListPrice?.toLocaleString()}
              </h2>
              <p className="mt-3 text-sm text-slate-500">
                <FontAwesomeIcon icon={faClock} className="mr-2 text-summit-highlight" />
                Listed {property?.OriginalEntryTimestamp && new Date(property.OriginalEntryTimestamp).toLocaleDateString()}
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-[1fr_360px] gap-10">
            <div className="space-y-10">
              <div className="overflow-hidden rounded-3xl bg-white shadow-xl p-4">
                {
                  loading ?
                    <div className="h-[600px] rounded-2xl bg-slate-100 animate-pulse" />
                    :
                    <ReactImageGallery
                      items={images}
                      loading={"eager"}
                      thumbnailPosition={`${windowSize > 950 ? "right" : "bottom"}`}
                      showThumbnails={windowSize > 950 ? true : false}
                      showPlayButton={false}
                    />
                }
              </div>

              <div className="rounded-3xl bg-white shadow-lg p-8">
                <h2 className="text-3xl font-heading text-summit-primary">
                  Property Features
                </h2>

                <div className="grid md:grid-cols-3 gap-6 mt-8">
                  {property?.PropertyType?.includes("Residential") && (
                    <>
                      <Feature
                        icon={faBed}
                        text={`${property?.BedroomsTotal || "--"} Bedrooms`}
                      />

                      <Feature
                        icon={faBath}
                        text={`${property?.BathroomsTotalInteger || "--"} Bathrooms`}
                      />
                    </>
                  )}

                  <Feature
                    icon={faRulerCombined}
                    text={`${property?.LivingAreaRange || property?.BuildingAreaTotal || "--"} Sq Ft`}
                  />
                </div>
              </div>

              <div className="rounded-3xl bg-white shadow-lg p-8">
                <h2 className="text-3xl font-heading text-summit-primary">
                  About This Property
                </h2>

                <p className="mt-5 leading-8 text-slate-600">
                  {
                    !detailsOpen && property?.PublicRemarks?.length > 700
                      ? property.PublicRemarks.substring(0, 700) + "..."
                      : property?.PublicRemarks
                  }
                </p>

                {
                  property?.PublicRemarks?.length > 700 &&
                  <button
                    onClick={() => setDetailsOpen(!detailsOpen)}
                    className="mt-5 text-summit-highlight font-semibold hover:underline"
                  >
                    {detailsOpen ? "Show Less" : "Read More"}
                  </button>
                }
              </div>

              <div className="rounded-3xl bg-white shadow-lg p-8">
                <h2 className="text-3xl font-heading text-summit-primary">
                  Additional Information
                </h2>

                <div className="grid md:grid-cols-2 gap-6 mt-6 text-slate-600">
                  <Info label="Property Type" value={property?.PropertyType} />
                  <Info label="Sub Type" value={property?.PropertySubType} />
                  <Info label="Heating" value={property?.HeatSource} />
                  <Info label="HVAC" value={property?.HeatType} />
                  <Info label="Garage" value={property?.GarageType} />
                  <Info label="Parking" value={property?.ParkingTotal} />
                  <Info label="Exposure" value={property?.DirectionFaces ?? "--"} />
                  <Info label="Lot" value={property?.LotSizeRangeAcres ?? "--" + " " + property?.LotSizeUnits} />
                  <Info label="Basement" value={property?.Basement?.length
                    ? property.Basement.join(", ")
                    : "--"} />
                  <Info label="Office" value={property?.ListOfficeName ?? "--"} />
                  <Info label="Board" value={property?.ListAOR ?? "--"} />
                </div>
              </div>
              <p className="mt-6 text-black text-lg font-light">{property?.PublicRemarksExtras}</p>

              <div>
                <h1 className="text-4xl font-heading text-summit-primary">
                  Affordability Calculator
                </h1>

                <div className={`w-full mt-5 mb-10 p-2 ${windowSize >= 1024 ? "bg-summit-background" : "bg-summit-background"}`}>
                  <iframe
                    src={`https://www.mortgagecalculator.org/webmasters/?downpayment=80000&homevalue=${property?.ListPrice}&loanamount=320000&interestrate=7&loanterm=30&propertytax=2700&pmi=1&homeinsurance=1500&monthlyhoa=0`}
                    scrolling="no"
                    style={{
                      width: "100%",
                      height: windowSize >= 1024
                        ? "2000px" // lg
                        : windowSize >= 768
                          ? "2000px" // md
                          : "2200px", // sm
                    }}
                  ></iframe>
                </div>
              </div>
            </div>


            <aside className="sticky top-28 h-fit space-y-6">
              <div className="rounded-3xl bg-white shadow-xl p-8 text-center">
                <img
                  src={getCurrentHeadshot()}
                  className="w-36 h-36 rounded-full mx-auto object-cover border-4 border-summit-highlight"
                  alt={getCurrentAgentName()}
                />

                <h3 className="mt-5 text-2xl font-heading text-summit-primary">
                  {getCurrentAgentName()}
                </h3>

                <div className="mt-5 space-y-3 text-slate-600">
                  <p>
                    <FontAwesomeIcon icon={faPhone} className="mr-2 text-summit-highlight" />
                    {getCurrentAgentNumber()}
                  </p>

                  <p>
                    <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-summit-highlight" />
                    {getCurrentAgentEmail()}
                  </p>
                </div>

              </div>

              <InquiryCard />

            </aside>
          </div>
        </div>
      </section>
    </>
  );
}

function Feature({ icon, text }) {
  return (
    <div className="flex items-center gap-4 rounded-2xl bg-slate-50 p-5">
      <FontAwesomeIcon icon={icon} className="text-3xl text-summit-highlight" />
      <p className="text-lg text-slate-600 font-medium">{text}</p>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="flex justify-between border-b pb-3">
      <span className="font-semibold text-slate-700">{label}</span>
      <span>{value || "--"}</span>
    </div>
  );
}

